/**
 * spectate.ts — PURE target selection for the online spectate camera (M74/M75).
 *
 * After a player finishes an online race, they watch the racers still out on
 * the course until the server declares the race over. Racing-game conventions:
 *   - AUTO-TARGET the LEADING still-racing seat (the one closest to the flag,
 *     i.e. next to finish). M75: the race now ends when the SECOND-TO-LAST
 *     racer finishes, so the trailing car gets cut off and never crosses on
 *     screen — watching the front of the pack means you actually see each
 *     finish, and the last one you watch is the finish that ends the race.
 *   - Only seats the LOBBY showed as filled are watchable — in matchmade rooms
 *     the staged AI seats count as filled, so the backfill disguise holds
 *     (owner-locked: AI fill is never revealed).
 *   - Manual cycling moves through the still-racing candidates in standings
 *     order and wraps.
 */

export interface SpectateRacer {
  id: number;
  finished: boolean;
}

/** The LEADING still-racing candidate (closest to finishing), or null when
 *  there's nobody left to watch. `standings` is leader-first (the game's
 *  standings() order). */
export function pickSpectateTarget(
  standings: readonly SpectateRacer[],
  seats: readonly number[],
  myId: number,
): number | null {
  for (const r of standings) {
    if (!r.finished && r.id !== myId && seats.includes(r.id)) return r.id;
  }
  return null;
}

/** The next/previous still-racing candidate from `currentId`, in standings
 *  order, wrapping. Falls back to the trailing pick when the current target
 *  is no longer watchable (finished / not a candidate). */
export function cycleSpectateTarget(
  standings: readonly SpectateRacer[],
  seats: readonly number[],
  myId: number,
  currentId: number,
  dir: 1 | -1,
): number | null {
  const candidates = standings.filter(
    (r) => !r.finished && r.id !== myId && seats.includes(r.id),
  );
  if (candidates.length === 0) return null;
  const at = candidates.findIndex((r) => r.id === currentId);
  if (at < 0) return pickSpectateTarget(standings, seats, myId);
  return candidates[(at + dir + candidates.length) % candidates.length].id;
}
